import { CopyIcon } from "@xola/icons";
import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, nullableString, requiredEmail, requiredString } from "common";
import { FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { Guide, Permission, UserShortCode } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { isXolaAdmin } from "../../../models/User";
import { AutoCompleteType, Form } from "../../Common/Form";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ChangeEmailButton, EmailType, isPendingOwnerChangeRequest } from "../../Dashboard/ChangeEmailButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { MultiFactorAuthenticationForm } from "./MultiFactorAuthenticationForm";
import { ValidatePasswordModal } from "./ValidatePasswordModal";
import { xolaUri } from "@/api/xolaUri";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useResource } from "@/hooks/useResource";
import { ChangePasswordPayload } from "@/models/ChangePassword";

interface FormValues {
    name: string;
    phone: string;
    email: string;
}

const validationSchema = object().shape({
    name: requiredString(),
    phone: nullableString().required(),
    email: requiredEmail(),
});

const initialValues: FormValues = {
    name: "",
    phone: "",
    email: "",
};

export const ProfileInfoForm = () => {
    const user = useUser();
    const seller = useSeller();
    const queryClient = useQueryClient();
    const MFARef = useRef<HTMLDivElement>(null);
    const { action } = useParams<{ action?: string }>();
    const { isGranted } = useAuthorization();
    const hasMFAAccess = !isXolaAdmin(user) && isGranted(Permission.ADD_MULTI_FACTOR_AUTHENTICATION_VIEW);

    useEffect(() => {
        if (action === "MFASetting") {
            MFARef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [action, MFARef]);

    const changePasswordFormRef = useRef<FormikProps<ChangePasswordPayload>>(null);
    const [isValidatePasswordModalOpen, setIsValidatePasswordModalOpen] = useState(false);
    const profileInfoFormRef = useRef<FormikProps<FormValues>>(null);
    const isPendingChangeEmailRequest = isPendingOwnerChangeRequest(user, seller);

    const { mutateAsync: updateProfile, isLoading } = useMutation(async (formValues: FormValues) => {
        return api.put(`users/${user.id}`, {
            ...formValues,
            email: user.email,
            ...(isPendingChangeEmailRequest || user.email === formValues.email ? {} : { newEmail: formValues.email }),
        });
    });

    const handleSubmit = async () => {
        setIsValidatePasswordModalOpen(true);
    };

    const handleProfileInfoFormSubmit = async () => {
        if (profileInfoFormRef?.current?.values) {
            setIsValidatePasswordModalOpen(false);
            const { name, email, phone } = profileInfoFormRef.current.values;
            await updateProfile({ name, email, phone });
            queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
            if (email === user.email) {
                flash.success("Profile details updated");
            } else {
                flash.success("Please check your email inbox for a confirmation email");
            }
        }
    };

    const MFAtooltip = (
        <>
            <p>MFA fields require OTP to make changes.</p>
            <p>Fields protected by MFA:</p>
            <p>1. MFA Phone Number</p>
            <p>2. Password change</p>
            <p>3. Routing number</p>
            <p>4. Account number</p>
        </>
    );

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                innerRef={profileInfoFormRef}
                validationSchema={validationSchema}
                initialValues={{ ...initialValues, ...user }}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, handleReset }: any) => {
                    return (
                        <Form autoComplete={AutoCompleteType.OFF}>
                            <h2 className="mt-4">My Profile</h2>
                            <ContentLayout.Section
                                title="Basic Information"
                                description="Update your basic information for your Xola profile"
                            >
                                <div className="space-y-4">
                                    {isPendingChangeEmailRequest && <ChangeEmailButton emailType={EmailType.PRIMARY} />}
                                    <FormField name="name" label="Name">
                                        <InputField />
                                    </FormField>
                                    <FormField name="email" label="Email">
                                        <InputField disabled={isPendingChangeEmailRequest} />
                                    </FormField>
                                    <FormField name="phone" label="Phone">
                                        <InputField isRequired />
                                    </FormField>
                                </div>

                                <div className="space-x-2 pt-4">
                                    <SubmitButton
                                        type="submit"
                                        data-testid="profile-info-form-save-button"
                                        isLoading={isLoading}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton type="reset" disabled={isLoading} onClick={handleReset}>
                                        Cancel
                                    </SecondaryButton>
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Section title="Passwords" description="Update your Xola password">
                                <ChangePasswordForm ref={changePasswordFormRef} />
                            </ContentLayout.Section>

                            {hasMFAAccess && (
                                <div ref={MFARef}>
                                    <ContentLayout.Section
                                        title="Multi Factor Authentication"
                                        description="MFA makes your account more secure by
                                    adding an additional verification to
                                    sensitive changes to your account."
                                        tooltip={MFAtooltip}
                                    >
                                        <MultiFactorAuthenticationForm />
                                    </ContentLayout.Section>
                                </div>
                            )}
                            {isGranted(Permission.DASHBOARD_VIEW) && (
                                <ContentLayout.Section
                                    title="Sync Calendar"
                                    description="Use this link to sync your Xola calendar with external calendars (like Apple's Calendar)."
                                >
                                    <SyncCalendarSection />
                                </ContentLayout.Section>
                            )}
                        </Form>
                    );
                }}
            </XolaFormik>
            <ValidatePasswordModal
                isOpen={isValidatePasswordModalOpen}
                onClose={() => {
                    setIsValidatePasswordModalOpen(false);
                }}
                onSuccess={async () => handleProfileInfoFormSubmit()}
            />
        </ContentLayout>
    );
};

const SyncCalendarSection = () => {
    const user = useUser();
    const seller = useSeller();
    const { data: guides, isLoading: isLoadingGuide } = useAutoFetchCollection<Guide>(["sellers", seller.id, "guides"]);
    const isGuide = guides.some((guide) => guide.user.id === user.id) ?? false;
    const shortCodeUserId = isLoadingGuide ? null : isGuide ? user.id : seller.id;
    const { data: userShortCode } = useResource<UserShortCode>(["users", shortCodeUserId, "code"], null, {
        enabled: !!shortCodeUserId,
    });

    const url = `${xolaUri}/calendar/${userShortCode?.shortCode}${isGuide ? "/guide" : ""}`;
    const handleUrlClick = () => {
        navigator.clipboard.writeText(url);
        flash.success(`${url} copied`);
    };

    if (isLoadingGuide) {
        return null;
    }

    return userShortCode ? (
        <div className="flex items-center align-middle">
            <span className="border-b border-primary text-primary">{url}</span>
            <SecondaryButton size="small" className="text-bold ml-4" onClick={handleUrlClick}>
                <CopyIcon className="mr-1" />
                Copy
            </SecondaryButton>
        </div>
    ) : (
        <div>
            You do not have permission to access the calendar link for{" "}
            <var data-var="user">{user?.name ?? user.id}</var>
        </div>
    );
};
